import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DollarSign, CheckCircle2, Clock, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

export const meta = {
  id: 'billing_queue',
  category: 'business',
  titleKey: 'dashboard.widgets.billingQueue',
  descriptionKey: 'dashboard.widgets.billingQueueDesc',
  defaultSize: 'lg',
  minRole: 'admin',
  icon: DollarSign,
};

const MONTHS_ES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function BillingQueueWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [markingPaid, setMarkingPaid] = useState(null);

  const { data: billingQueue } = useQuery({
    queryKey: ['billing-queue'],
    queryFn: () => api.get('/dashboard/billing-queue').then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  if (!billingQueue?.companies?.length) return null;

  const pending = billingQueue.companies.filter(c => c.is_due && !c.is_billed);
  const upcoming = billingQueue.companies.filter(c => !c.is_due && !c.is_billed);
  const billed = billingQueue.companies.filter(c => c.is_billed);

  const markBillingPaid = async (company) => {
    if (markingPaid) return;
    if (!window.confirm(t('dashboard.confirmMarkPaid', `¿Marcar como cobrado ${company.name}? Se creará un cobro pagado por ${company.currency} ${parseFloat(company.monthly_rate).toLocaleString('es-AR')}.`))) return;
    setMarkingPaid(company.id);
    try {
      const now = new Date();
      await api.post('/invoices', {
        document_type: 'cobro',
        company_id: company.id,
        total: company.monthly_rate,
        currency: company.currency || 'ARS',
        description: `Abono mensual ${MONTHS_ES[now.getMonth() + 1]} ${now.getFullYear()}`,
        status: 'paid',
        issue_date: now.toISOString().split('T')[0],
      });
      toast.success(t('dashboard.markedAsPaid', 'Cobro creado y marcado como pagado'));
      queryClient.invalidateQueries({ queryKey: ['billing-queue'] });
    } catch (err) {
      toast.error(err.response?.data?.error || t('common.actionFailed'));
    } finally {
      setMarkingPaid(null);
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DollarSign size={16} />
          {t('dashboard.billingQueue', 'Facturación')} — {MONTHS_ES[billingQueue.currentMonth]} {billingQueue.currentYear}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {pending.length > 0 && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#fef2f2', color: '#ef4444', fontWeight: 600 }}>
              {pending.length} {t('dashboard.pendingBilling', 'pendientes')}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/companies?tab=subscriptions')} style={{ fontSize: 11 }}>
            {t('dashboard.viewAll', 'Ver todo')} →
          </button>
        </div>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {pending.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderBottom: '1px solid var(--c-border-subtle)', cursor: 'pointer' }}
            onClick={() => navigate(`/reports?company_id=${c.id}&invoice_day=${c.invoice_day}`)}>
            <div style={{ width: 4, height: 28, borderRadius: 2, background: '#ef4444', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                {t('dashboard.invoiceDay', 'Día')} {c.invoice_day} · {c.currency} {parseFloat(c.monthly_rate).toLocaleString('es-AR')}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 8px', flexShrink: 0, color: '#22c55e' }}
              title={t('dashboard.markAsPaid', 'Marcar como cobrado')}
              disabled={markingPaid === c.id}
              onClick={e => { e.stopPropagation(); markBillingPaid(c); }}>
              {markingPaid === c.id ? <Clock size={13} className="spin" /> : <CheckCircle2 size={13} />}
            </button>
            <button className="btn btn-primary btn-sm" style={{ fontSize: 11, padding: '4px 10px', flexShrink: 0 }}
              onClick={e => { e.stopPropagation(); navigate(`/reports?company_id=${c.id}&invoice_day=${c.invoice_day}`); }}>
              <FileText size={11} /> {t('dashboard.invoice', 'Facturar')}
            </button>
          </div>
        ))}
        {upcoming.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderBottom: '1px solid var(--c-border-subtle)', opacity: 0.6 }}>
            <div style={{ width: 4, height: 28, borderRadius: 2, background: 'var(--c-border)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                {t('dashboard.invoiceDay', 'Día')} {c.invoice_day} · {c.currency} {parseFloat(c.monthly_rate).toLocaleString('es-AR')}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 8px', flexShrink: 0, color: '#22c55e' }}
              title={t('dashboard.markAsPaid', 'Marcar como cobrado')}
              disabled={markingPaid === c.id}
              onClick={e => { e.stopPropagation(); markBillingPaid(c); }}>
              {markingPaid === c.id ? <Clock size={13} className="spin" /> : <CheckCircle2 size={13} />}
            </button>
            <span style={{ fontSize: 11, color: 'var(--c-text-muted)', flexShrink: 0 }}>
              <Clock size={10} /> {t('dashboard.dueIn', 'en')} {c.invoice_day - billingQueue.today}d
            </span>
          </div>
        ))}
        {billed.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderBottom: '1px solid var(--c-border-subtle)', opacity: 0.5 }}>
            <div style={{ width: 4, height: 28, borderRadius: 2, background: '#22c55e', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'line-through' }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                {c.invoice_number_this_month || '✓'}
              </div>
            </div>
            <CheckCircle2 size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
